/**
 * SubscriptionRow — shared <tr> row for admin subscription tables.
 *
 * Used by:
 *  - `BillingSubscriptions.tsx` (global admin list — Tenant column visible)
 *  - `TenantDetails.tsx` SubscriptionsTab (`hideTenantColumn={true}`, AC-C9)
 *
 * Preserves prior behaviour:
 *  - `opacity-60` for SUPERSEDED (AC-C7).
 *  - `role="button"`, `tabIndex={0}`, Enter/Space keyboard activation (AC-C8).
 *  - `data-testid="subscription-row-${id}"` for test selectors.
 *
 * @see docs/specifications/2026-05-14-tenant-detail-billing-tabs/spec.md AC-S4
 * @see docs/specifications/2026-09-28-wybor-pakietu-na-kolejny-okres/spec.md AC-12 ("Pakiet na kolejny okres")
 */
import React from "react";
import {
  isOverdueScheduled,
  STATUS_BADGE_STYLES,
  STATUS_LABELS,
} from "./subscription-display.constants";
import { getBillingIntervalLabel } from "./entitlement-formatter";
import type { BillingSubscription } from "../../../../types/adminBilling";

/**
 * AC-12: the renewal preference can only change for an ACTIVE ordering-model
 * subscription with auto-renewal on — every other state is rejected by the
 * backend block policy (AC-5), so the action is not offered there.
 */
const canChooseRenewalPackage = (sub: BillingSubscription): boolean =>
  sub.status === "ACTIVE" && !sub.isLegacy && sub.autoRenewal !== false;

export interface SubscriptionRowProps {
  subscription: BillingSubscription;
  hideTenantColumn?: boolean;
  onSelectDetail: (id: string) => void;
  onMigrate?: (id: string) => void;
  /** AC-12: opens the "Pakiet na kolejny okres" modal; omit to hide the action. */
  onChangeRenewalPackage?: (id: string) => void;
  rowRef?: (el: HTMLTableRowElement | null) => void;
  migrateButtonRef?: (el: HTMLButtonElement | null) => void;
}

export const SubscriptionRow: React.FC<SubscriptionRowProps> = ({
  subscription: sub,
  hideTenantColumn,
  onSelectDetail,
  onMigrate,
  onChangeRenewalPackage,
  rowRef,
  migrateButtonRef,
}) => {
  // AC-8d: same overdue flag as SubscriptionDetailModal so the amber badge
  // is visible in the list/row view, not only after opening the modal.
  const overdue = isOverdueScheduled(sub);
  return (
    <tr
      ref={rowRef}
      className={`hover:bg-neutral-50 cursor-pointer ${
        sub.status === "SUPERSEDED" ? "opacity-60" : ""
      }`}
      onClick={() => onSelectDetail(sub.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelectDetail(sub.id);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Szczegóły subskrypcji ${
        sub.product?.name ?? sub.tenantName ?? sub.id
      }`}
      data-testid={`subscription-row-${sub.id}`}
    >
      {!hideTenantColumn && (
        <td className="px-6 py-4 whitespace-nowrap">
          {sub.tenantName ? (
            <span className="text-sm text-neutral-800">{sub.tenantName}</span>
          ) : (
            <span className="text-xs font-mono text-neutral-500">
              {sub.tenantId.slice(0, 8)}...
            </span>
          )}
        </td>
      )}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700">
        {sub.product?.name ?? (
          <span className="text-neutral-400 italic">Brak produktu</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            overdue
              ? "bg-amber-100 text-amber-800"
              : STATUS_BADGE_STYLES[sub.status] || "bg-blue-100 text-blue-800"
          }`}
        >
          {overdue
            ? "Zaplanowana (opóźniona)"
            : STATUS_LABELS[sub.status] || sub.status}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700">
        {sub.status === "SCHEDULED" && sub.currentPeriodStart ? (
          // "Aktywna do" nie ma sensu dla SCHEDULED (jeszcze nieaktywna) —
          // pokazujemy datę startu zamiast końca przyszłego okresu, zgodnie
          // z User Story #1 ("Aktywna do 19.07" + "Zaplanowana od 19.07").
          <span title="Data startu — subskrypcja jeszcze nieaktywna">
            od{" "}
            {new Date(sub.currentPeriodStart).toLocaleDateString("pl-PL", {
              day: "2-digit",
              month: "2-digit",
            })}
          </span>
        ) : sub.currentPeriodEnd ? (
          new Date(sub.currentPeriodEnd).toLocaleDateString("pl-PL")
        ) : (
          <span className="text-neutral-400">—</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
        {getBillingIntervalLabel(
          sub.status,
          sub.billingInterval,
          sub.product?.trialDurationDays
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {sub.isLegacy ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            Legacy
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Nowy
          </span>
        )}
      </td>
      <td
        className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => onSelectDetail(sub.id)}
          className="text-primary-600 hover:text-primary-800 font-medium"
        >
          Szczegóły
        </button>
        {sub.isLegacy && onMigrate && (
          <button
            type="button"
            ref={migrateButtonRef}
            onClick={() => onMigrate(sub.id)}
            className="text-amber-600 hover:text-amber-800 font-medium"
          >
            Migruj
          </button>
        )}
        {onChangeRenewalPackage && canChooseRenewalPackage(sub) && (
          <button
            type="button"
            onClick={() => onChangeRenewalPackage(sub.id)}
            className="text-primary-600 hover:text-primary-800 font-medium"
          >
            Pakiet na kolejny okres
          </button>
        )}
      </td>
    </tr>
  );
};

export interface SubscriptionTableHeadProps {
  hideTenantColumn?: boolean;
}

export const SubscriptionTableHead: React.FC<SubscriptionTableHeadProps> = ({
  hideTenantColumn,
}) => {
  const thClass =
    "px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider";
  return (
    <thead className="bg-neutral-50">
      <tr>
        {!hideTenantColumn && (
          <th scope="col" className={thClass}>
            Tenant
          </th>
        )}
        <th scope="col" className={thClass}>
          Produkt
        </th>
        <th scope="col" className={thClass}>
          Status
        </th>
        <th scope="col" className={thClass}>
          Aktywna do
        </th>
        <th scope="col" className={thClass}>
          Interwał
        </th>
        <th scope="col" className={thClass}>
          Typ
        </th>
        <th
          scope="col"
          className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider"
        >
          Akcje
        </th>
      </tr>
    </thead>
  );
};
